import React, { useState, useEffect } from 'react';
import {
    TableContainer, Typography, Table, Chip, TableHead, TableRow,
    TableCell, TableBody, Button, IconButton, Box, TextField,
    Paper, Stack, alpha, Divider,
} from '@mui/material';
import { Edit, Check, Close, VisibilityOutlined } from '@mui/icons-material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axios from 'axios';
import { BASE_URL } from '../common/constant';
import toast from 'react-hot-toast';
import { PRIMARY } from 'themes/colors';
import { getRole, getToken } from 'Service/Service';
import { ConfirmDialogContext } from '../common/ConfirmDialogProvider';

interface IRecord {
    _id: string;
    driverName: string;
    invoiceNo: string;
    invoiceDate: string;
    items: Array<{
        item: string;
        shippedQuantity: number;
        unitPrice: number;
        totalPrice: number;
    }>;
    remarks?: string;
    status: string;
}

const GREEN = PRIMARY.main;
const GREEN_DARK = PRIMARY.dark;
const GREEN_LIGHT = PRIMARY.light;

const statusChip = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'accept')
        return { label: 'Accept', bg: alpha('#4caf50', 0.1), color: '#2e7d32' };
    if (s === 'release')
        return { label: 'Release', bg: alpha('#ff9800', 0.1), color: '#e65100' };
    if (s === 'sale_captured')
        return { label: 'Captured', bg: alpha('#9e9e9e', 0.12), color: '#616161' };
    return { label: status, bg: alpha('#2196f3', 0.1), color: '#1565c0' };
};

const UpdateTable = ({ records }: { records: IRecord[] }) => {
    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const [originalValues, setOriginalValues] = useState<{ [key: string]: IRecord }>({});
    const [data, setData] = useState<IRecord[]>([]);
    const [currentRecord, setCurrentRecord] = useState<Partial<IRecord>>({});
    const [updatedRecord, setUpdatedRecord] = useState<Partial<IRecord>>({});
    const [showDetails, setShowDetails] = useState(false);
    const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

    useEffect(() => { setData(records); }, [records]);

    const handleViewDetails = (record: IRecord) => {
        setUpdatedRecord(record);
        setShowDetails(true);
    };

    const handleActionEdit = (id: string) => {
        const record = data.find(r => r._id === id);
        if (record) {
            setOriginalValues(prev => ({ ...prev, [id]: record }));
            setCurrentRecord(record);
            setEditingRowId(id);
        }
    };

    const cancelEdit = () => {
        if (editingRowId) {
            setData(prev => prev.map(r => r._id === editingRowId ? originalValues[editingRowId] : r));
            setEditingRowId(null);
            setCurrentRecord({});
        }
    };

    const handleChange = (event: any) => {
        const { name, value } = event.target;
        const parsedValue = isNaN(parseFloat(value)) ? value : parseFloat(value);
        setCurrentRecord((prev: any) => {
            const updated = { ...prev, [name]: parsedValue };
            if (name === 'shippedQuantity' || name === 'unitPrice') {
                const q = name === 'shippedQuantity' ? parsedValue : prev.shippedQuantity || 0;
                const p = name === 'unitPrice' ? parsedValue : prev.unitPrice || 0;
                updated.totalPrice = q * p;
            }
            return updated;
        });
    };

    const sanitizeRecord = (record: any): any => {
        const { createdAt, updatedAt, __v, tableData, ...sanitizedRecord } = record;
        sanitizedRecord.status = getRole() === 'admin' ? 'release' : 'accept';
        return sanitizedRecord;
    };

    const saveInvoice = async () => {
        if (!updatedRecord._id) return toast('Select a record to update', { icon: '⚠️' });
        showConfirmDialog('Are you sure to save this record', 'Do you want to save this entry?', async () => {
            const sanitizedRecord = sanitizeRecord(updatedRecord);
            const response = await axios.patch(BASE_URL + `invoice/${sanitizedRecord._id}`, {}, {
                headers: { encryption: false, token: getToken() },
            });
            if (response.data?.data) {
                setData([]);
                setUpdatedRecord({});
                toast.success(response.data.message);
            }
        });
        setEditingRowId(null);
        setUpdatedRecord({});
    };

    const saveEdit = async () => {
        if (!currentRecord._id) return toast('Select a record to update', { icon: '⚠️' });
        showConfirmDialog('Are you sure to save this invoice', 'Do you want to accept this entry?', async () => {
            const sanitizedRecord = sanitizeRecord(currentRecord);
            const response = await axios.patch(BASE_URL + `invoice/${sanitizedRecord._id}`, sanitizedRecord, {
                headers: { encryption: false, token: getToken() },
            });
            if (response.data?.data) {
                setData(prev => prev.map(r => r._id === currentRecord._id ? { ...r, ...currentRecord } : r));
                setEditingRowId(null);
                setCurrentRecord({});
                toast.success(response.data?.data.message);
            }
        });
    };

    const handleButtonClick = (recordId: string) => {
        const record = data.find(r => r._id === recordId);
        if (record) handleViewDetails(record);
    };

    if (!data.length) return null;

    const thSx = {
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.73rem',
        letterSpacing: 0.4,
        textTransform: 'uppercase' as const,
        borderBottom: 'none',
        py: 1.4,
        whiteSpace: 'nowrap' as const,
    };
    const tdSx = { fontSize: '0.82rem', py: 1.1, borderColor: 'grey.100', whiteSpace: 'nowrap' as const };

    return (
        <Box>
            {/* ── Invoice Details Table ── */}
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden', mb: 3 }}>
                {/* Table header bar */}
                <Box
                    sx={{
                        background: `linear-gradient(90deg, ${GREEN_DARK} 0%, ${GREEN_LIGHT} 100%)`,
                        px: 2.5, py: 1.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                        <ReceiptLongIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff', fontSize: '0.82rem', letterSpacing: 0.3 }}>
                            Invoice Details
                        </Typography>
                    </Stack>
                    <Chip
                        label={`${data.length} invoice${data.length > 1 ? 's' : ''}`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.63rem', fontWeight: 600, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }}
                    />
                </Box>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ background: `linear-gradient(90deg, ${GREEN_DARK} 0%, ${GREEN_LIGHT} 100%)` }}>
                                <TableCell sx={thSx}>Driver</TableCell>
                                <TableCell sx={thSx}>Invoice No</TableCell>
                                <TableCell sx={thSx}>Date</TableCell>
                                <TableCell sx={thSx}>Status</TableCell>
                                <TableCell align="right" sx={thSx}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((record, idx) => {
                                const chip = statusChip(record.status);
                                const isEditing = editingRowId === record._id;
                                return (
                                    <TableRow
                                        key={record._id}
                                        sx={{
                                            bgcolor: idx % 2 === 0 ? '#fff' : '#f8faf8',
                                            '&:hover': { bgcolor: alpha(GREEN_LIGHT, 0.04) },
                                            transition: 'background-color 0.15s',
                                            '& td': { ...tdSx },
                                        }}
                                    >
                                        {isEditing ? (
                                            <>
                                                <TableCell>
                                                    <TextField
                                                        size="small" name="driverName"
                                                        value={currentRecord.driverName || ''}
                                                        disabled
                                                        sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small" name="invoiceNo"
                                                        value={currentRecord.invoiceNo || ''}
                                                        InputProps={{ readOnly: true }}
                                                        disabled
                                                        sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(record.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={chip.label}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: chip.bg, color: chip.color }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                                        <Button
                                                            size="small" variant="contained" onClick={saveEdit}
                                                            startIcon={<Check />}
                                                            sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600, bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DARK }, boxShadow: 'none', fontSize: '0.75rem' }}
                                                        >
                                                            Accept
                                                        </Button>
                                                        <Button
                                                            size="small" variant="outlined" color="inherit" onClick={cancelEdit}
                                                            startIcon={<Close />}
                                                            sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600, fontSize: '0.75rem' }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Stack>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                                        <LocalShippingIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                                        <Typography variant="body2" fontWeight={600}>{record.driverName}</Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700} color={GREEN_DARK}>
                                                        #{record.invoiceNo}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(record.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={chip.label}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: chip.bg, color: chip.color }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {record.status.toUpperCase() !== 'ACCEPT' && (
                                                        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                                            <Button
                                                                size="small" variant="outlined"
                                                                onClick={() => handleButtonClick(record._id)}
                                                                startIcon={<VisibilityOutlined />}
                                                                sx={{
                                                                    textTransform: 'none', borderRadius: 1.5, fontWeight: 600,
                                                                    fontSize: '0.72rem',
                                                                    borderColor: alpha(GREEN_LIGHT, 0.4), color: GREEN_DARK,
                                                                    '&:hover': { borderColor: GREEN_LIGHT, bgcolor: alpha(GREEN_LIGHT, 0.05) },
                                                                }}
                                                            >
                                                                Details
                                                            </Button>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleActionEdit(record._id)}
                                                                sx={{
                                                                    bgcolor: alpha(GREEN_LIGHT, 0.08), color: GREEN,
                                                                    borderRadius: 1.5,
                                                                    '&:hover': { bgcolor: alpha(GREEN_LIGHT, 0.18) },
                                                                }}
                                                            >
                                                                <Edit sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Stack>
                                                    )}
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* ── Invoice Items Detail ── */}
            {showDetails && updatedRecord.items && (
                <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden', mb: 3 }}>
                    <Box
                        sx={{
                            background: `linear-gradient(90deg, ${GREEN_DARK} 0%, ${GREEN_LIGHT} 100%)`,
                            px: 2.5, py: 1.5,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                            <ReceiptLongIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff', fontSize: '0.82rem' }}>
                                Invoice #{updatedRecord.invoiceNo} — Items
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Chip
                                label={`${updatedRecord.items.length} item${updatedRecord.items.length > 1 ? 's' : ''}`}
                                size="small"
                                sx={{ height: 20, fontSize: '0.63rem', fontWeight: 600, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }}
                            />
                            <Button
                                size="small" variant="contained" onClick={saveInvoice}
                                sx={{
                                    textTransform: 'none', borderRadius: 1.5, fontWeight: 700,
                                    bgcolor: 'rgba(255,255,255,0.22)', color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.35)',
                                    fontSize: '0.72rem', py: 0.3, px: 1.5,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                    boxShadow: 'none',
                                }}
                            >
                                Save &amp; Accept
                            </Button>
                        </Stack>
                    </Box>

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ background: `linear-gradient(90deg, ${GREEN_DARK} 0%, ${GREEN_LIGHT} 100%)` }}>
                                    {['Item', 'Shipped Qty', 'Unit Price', 'Total Price'].map((h) => (
                                        <TableCell key={h} sx={thSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {updatedRecord.items.map((item, idx) => (
                                    <TableRow
                                        key={idx}
                                        sx={{
                                            bgcolor: idx % 2 === 0 ? '#fff' : '#f8faf8',
                                            '&:hover': { bgcolor: alpha(GREEN_LIGHT, 0.04) },
                                            '& td': { ...tdSx },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="caption" fontWeight={700} color={GREEN_DARK}>{item.item}</Typography>
                                        </TableCell>
                                        <TableCell>{item.shippedQuantity}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>₹{item.unitPrice}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={700} color={GREEN_DARK}>₹{item.totalPrice}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: alpha(GREEN_LIGHT, 0.06) }}>
                                    <TableCell colSpan={3}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">Grand Total</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={800} color={GREEN_DARK}>
                                            ₹{updatedRecord.items.reduce((s, i) => s + i.totalPrice, 0)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
};

export default UpdateTable;
